import { Alert, Button, Input } from "antd"
import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import styled from "styled-components"

import { trackEvent } from "lib/eventTracker"

import {
  postPasswordResetRequest,
  putPasswordReset,
} from "../actions/passwordAuth"
import PasswordAuthLayout from "../components/PasswordAuth/PasswordAuthLayout"
import {
  PASSWORD_MIN_LENGTH,
  isValidEmail,
  passwordProblem,
  resetErrorKey,
} from "../helpers/passwordAuth"
import { useOneTimeTokenFromUrl } from "../hooks/useOneTimeTokenFromUrl"
import { t } from "../lib/i18n"
import routes from "../router/routes"

// メールのリンク（auth-api の Users::DeviseNotification::RESET_PATH）は
// `/password/reset?reset_password_token=...`。token があれば新しいパスワードの
// 入力、無ければリセットメールの申請を出す。
const PasswordReset = (): JSX.Element => {
  // URL から消す effect は trackEvent より先に宣言する
  const token = useOneTimeTokenFromUrl("reset_password_token")

  useEffect(() => {
    trackEvent({ eventName: "Visit Password Reset Page" })
  }, [])

  return (
    <PasswordAuthLayout
      title={t(token ? "password_reset_new_title" : "password_reset_title")}
    >
      {token ? <NewPasswordForm token={token} /> : <ResetRequestForm />}
      <Link to={routes.Login.path}>{t("back_to_login")}</Link>
    </PasswordAuthLayout>
  )
}

const ResetRequestForm = (): JSX.Element => {
  const [email, setEmail] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)
  const [errorKey, setErrorKey] = useState<string | null>(null)

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!isValidEmail(email)) {
      setErrorKey("invalid_email")
      return
    }

    setSubmitting(true)
    setErrorKey(null)
    const result = await postPasswordResetRequest(email.trim())
    setSubmitting(false)

    if (result.ok) {
      setSent(true)
      return
    }
    setErrorKey(resetErrorKey(result))
  }

  // 登録の有無に関わらず 200 なので「登録されていれば届く」と書く
  if (sent) {
    return <Alert type="success" message={t("password_reset_request_sent")} />
  }

  return (
    <StyledForm onSubmit={onSubmit} noValidate>
      <p>{t("password_reset_request_description")}</p>
      <Input
        type="email"
        aria-label={t("email")}
        placeholder={t("email")}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="email"
      />
      {errorKey && <Alert type="error" message={t(errorKey)} />}
      <Button type="primary" htmlType="submit" loading={submitting} block>
        {t("password_reset_request_submit")}
      </Button>
    </StyledForm>
  )
}

const NewPasswordForm = ({ token }: { token: string }): JSX.Element => {
  const [password, setPassword] = useState("")
  const [passwordConfirmation, setPasswordConfirmation] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [errorKey, setErrorKey] = useState<string | null>(null)

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    const problem = passwordProblem(password)
    if (problem) {
      setErrorKey(problem)
      return
    }
    if (password !== passwordConfirmation) {
      setErrorKey("password_mismatch")
      return
    }

    setSubmitting(true)
    setErrorKey(null)
    const result = await putPasswordReset({
      resetPasswordToken: token,
      password,
      passwordConfirmation,
    })
    setSubmitting(false)

    if (result.ok) {
      setDone(true)
      return
    }
    setErrorKey(resetErrorKey(result))
  }

  if (done) {
    return <Alert type="success" message={t("password_reset_done")} />
  }

  return (
    <StyledForm onSubmit={onSubmit} noValidate>
      <Input.Password
        aria-label={t("password")}
        placeholder={t("password_rule", String(PASSWORD_MIN_LENGTH))}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="new-password"
      />
      <Input.Password
        aria-label={t("password_confirmation")}
        placeholder={t("password_confirmation")}
        value={passwordConfirmation}
        onChange={(e) => setPasswordConfirmation(e.target.value)}
        autoComplete="new-password"
      />
      {errorKey && (
        <Alert
          type="error"
          message={t(errorKey, String(PASSWORD_MIN_LENGTH))}
        />
      )}
      {errorKey === "password_reset_link_invalid" && (
        <Link to={routes.PasswordReset.path}>
          {t("password_reset_request_again")}
        </Link>
      )}
      <Button type="primary" htmlType="submit" loading={submitting} block>
        {t("password_reset_submit")}
      </Button>
    </StyledForm>
  )
}

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px 0;
  width: 100%;
`

export default PasswordReset
